/**
 * The rider directory.
 *
 * One row per rider, over the same `riders` table that deliveries, remittance,
 * settlements, bikes and hire purchase all already point at. Everything on the
 * right — current bike, tracker, what they owe, how far through an agreement —
 * is read from those tables rather than stored here, so the directory cannot
 * drift away from the records it describes.
 *
 * There is no delete. Remove deactivates, and the dialog says exactly what
 * survives, because a rider with three hundred deliveries is the explanation
 * for all of them.
 */

import { useState, type ReactNode } from 'react';
import { AlertTriangle, Bike as BikeIcon, HandCoins, Pencil, Plus, RotateCcw, Search, UserMinus } from 'lucide-react';
import {
  HIRE_PURCHASE_STATUS_LABELS,
  normalizeNigerianPhone,
  RIDER_STATUS_LABELS,
  type RiderDirectoryRow,
} from '@cenox/shared';
import {
  ActionMenu,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  MenuItem,
  MenuSeparator,
  Modal,
  Select,
  TableSkeleton,
  Textarea,
} from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { ApiError } from '../../lib/api';
import { money, todayIn } from '../../lib/format';
import { AssignBikeDialog, RIDER_BIKE_OWNERSHIP } from './AssignBikeDialog';
import { ClearDebtDialog } from './ClearDebtDialog';
import { SectionTitle, Stat } from './parts';
import {
  useCreateRider,
  useRemoveRider,
  useRestoreRider,
  useRiderDirectory,
  useRiderEntanglements,
  useUnmatchedNames,
  useUpdateRider,
} from './riderData';

type StatusFilter = 'active' | 'suspended' | 'inactive' | 'all';

const FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'active', label: 'Active riders' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'inactive', label: 'Archived (removed)' },
  { value: 'all', label: 'All' },
];

type OwnershipFilter = '' | 'rider' | 'cenohub' | 'investor' | 'unassigned';
const OWNERSHIP_FILTERS: Array<{ value: OwnershipFilter; label: string }> = [
  { value: '', label: 'Any bike ownership' },
  { value: 'rider', label: 'Self Bike' },
  { value: 'cenohub', label: 'Company Bike' },
  { value: 'investor', label: 'Investor Bike' },
  { value: 'unassigned', label: 'Unassigned' },
];

/** "Self Bike", "Company Bike", "Investor Bike · Ada", or "Unassigned" — never a guess. */
export function bikeOwnershipLabel(r: Pick<RiderDirectoryRow, 'bikeId' | 'bikeOwnership' | 'bikeInvestorName'>): string {
  if (!r.bikeId || !r.bikeOwnership) return 'Unassigned';
  const base = RIDER_BIKE_OWNERSHIP[r.bikeOwnership];
  return r.bikeOwnership === 'investor' && r.bikeInvestorName ? `${base} · ${r.bikeInvestorName}` : base;
}

export function RidersPage(): ReactNode {
  const { business, can } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';

  const [status, setStatus] = useState<StatusFilter>('active');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<RiderDirectoryRow | 'new' | null>(null);
  const [removing, setRemoving] = useState<RiderDirectoryRow | null>(null);
  const [viewing, setViewing] = useState<RiderDirectoryRow | null>(null);
  const [ownership, setOwnership] = useState<OwnershipFilter>('');
  // By id, so the dialog reads the row as it is after each save.
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [clearingId, setClearingId] = useState<string | null>(null);

  const directory = useRiderDirectory({ status, ...(search ? { search } : {}), ...(ownership ? { ownership } : {}) });
  const restore = useRestoreRider();
  const mayManage = can('deliveries:manage');
  const mayManageMoney = can('remittance:manage');
  const mayAssign = can('bikes:manage');

  if (directory.isError) {
    return <ErrorState message="Could not load riders." onRetry={() => void directory.refetch()} />;
  }

  const rows = directory.data?.data ?? [];
  const owed = rows.reduce((total, r) => total + Number(r.remittanceOutstanding), 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Riders" count={rows.length} hint={FILTERS.find((f) => f.value === status)?.label} />
        <Stat label="On a bike" count={rows.filter((r) => r.bikeId !== null).length} />
        <Stat
          label="Buying a bike"
          count={rows.filter((r) => r.hirePurchaseStatus !== null).length}
        />
        <Stat
          label="Remittance owed"
          value={owed.toFixed(2)}
          currency={currency}
          tone={owed > 0 ? 'owed' : 'plain'}
        />
      </div>

      <section>
        <SectionTitle
          aside={
            mayManage ? (
              <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>
                Add rider
              </Button>
            ) : undefined
          }
        >
          Riders
        </SectionTitle>

        <div className="mb-3 flex flex-wrap items-start gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, rider ID or phone"
              aria-label="Search riders"
              className="pl-9"
            />
          </div>
          <div className="min-w-[150px]">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
              aria-label="Filter riders"
              options={FILTERS}
            />
          </div>
          <div className="min-w-[170px]">
            <Select
              value={ownership}
              onChange={(e) => setOwnership(e.target.value as OwnershipFilter)}
              aria-label="Filter by bike ownership"
              options={OWNERSHIP_FILTERS}
            />
          </div>
        </div>

        {directory.isPending ? (
          <TableSkeleton rows={6} columns={7} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={search ? 'No riders match' : 'No riders yet'}
            description="A rider is recorded once. Deliveries, remittance, bikes and hire purchase all point at that one record."
          />
        ) : (
          <Card>
            <div className="-mx-3 overflow-x-auto px-3">
              <table className="w-full min-w-[980px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3 font-medium">Rider</th>
                    <th className="py-2 pr-3 font-medium">Rider ID</th>
                    <th className="py-2 pr-3 font-medium">Phone</th>
                    <th className="py-2 pr-3 font-medium">Location</th>
                    <th className="py-2 pr-3 font-medium">Bike / GPS</th>
                    <th className="py-2 pr-3 font-medium">Bike ownership</th>
                    <th className="py-2 pr-3 text-right font-medium">Remittance owed</th>
                    <th className="py-2 pr-3 font-medium">Hire purchase</th>
                    <th className="py-2 pr-3 font-medium">Joined</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    {/* Pinned to the right so the actions never need a sideways scroll. */}
                    <th className="sticky right-0 bg-white py-2 pl-2 text-right font-medium shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.15)]">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.id} className={r.status === 'inactive' ? 'text-slate-400' : ''}>
                      <td className="py-2 pr-3">
                        <button
                          type="button"
                          className="font-medium text-slate-900 hover:underline"
                          onClick={() => setViewing(r)}
                        >
                          {r.canonicalName}
                        </button>
                        {r.aliases.length > 0 && (
                          <span className="block text-xs text-slate-400">
                            also {r.aliases.join(', ')}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs text-slate-600">
                        {r.externalId ?? '—'}
                      </td>
                      <td className="py-2 pr-3 text-slate-600">{r.phone ?? '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">
                        {r.branchName ?? <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-2 pr-3 text-xs text-slate-600">
                        {r.bikeRegistration ? (
                          <>
                            <span className="inline-flex items-center gap-1">
                              <BikeIcon className="size-3.5" aria-hidden />
                              {r.bikeRegistration}
                            </span>
                            <span className="block font-mono text-slate-400">
                              {r.gpsImei ?? 'no tracker'}
                            </span>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        <span className={r.bikeId ? 'font-medium text-slate-700' : 'text-slate-400'}>
                          {bikeOwnershipLabel(r)}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {Number(r.remittanceOutstanding) > 0 ? (
                          <span className="text-rose-700">
                            {money(r.remittanceOutstanding, currency)}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        {r.hirePurchaseStatus ? (
                          <>
                            <Badge tone="info">
                              {HIRE_PURCHASE_STATUS_LABELS[r.hirePurchaseStatus]}
                            </Badge>
                            <span className="mt-0.5 block tabular-nums text-slate-500">
                              {money(r.hirePurchaseOutstanding ?? '0', currency)} left
                            </span>
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs text-slate-500">{r.joinedOn ?? '—'}</td>
                      <td className="py-2 pr-3">
                        <Badge
                          tone={
                            r.status === 'active'
                              ? 'success'
                              : r.status === 'suspended'
                                ? 'warning'
                                : 'neutral'
                          }
                        >
                          {RIDER_STATUS_LABELS[r.status]}
                        </Badge>
                      </td>
                      <td className="sticky right-0 bg-white py-1.5 pl-2 text-right shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.15)]">
                        {mayManage || mayAssign || mayManageMoney ? (
                          <ActionMenu label={`Actions for ${r.canonicalName}`}>
                            {r.status !== 'inactive' ? (
                              <>
                                {mayManage ? (
                                  <MenuItem icon={<Pencil className="size-4" />} onSelect={() => setEditing(r)}>
                                    Edit rider
                                  </MenuItem>
                                ) : null}
                                {mayAssign ? (
                                  <MenuItem icon={<BikeIcon className="size-4" />} onSelect={() => setAssigningId(r.id)}>
                                    Assign / change bike
                                  </MenuItem>
                                ) : null}
                                {mayManageMoney ? (
                                  <MenuItem icon={<HandCoins className="size-4" />} onSelect={() => setClearingId(r.id)}>
                                    Clear / adjust debt
                                  </MenuItem>
                                ) : null}
                                {mayManage && mayManageMoney ? (
                                  <>
                                    <MenuSeparator />
                                    <MenuItem
                                      icon={<UserMinus className="size-4" />}
                                      tone="danger"
                                      onSelect={() => setRemoving(r)}
                                    >
                                      Remove rider
                                    </MenuItem>
                                  </>
                                ) : null}
                              </>
                            ) : (
                              <>
                                {mayManage ? (
                                  <MenuItem
                                    icon={<RotateCcw className="size-4" />}
                                    disabled={restore.isPending}
                                    onSelect={() => restore.mutate(r.id)}
                                  >
                                    Reactivate rider
                                  </MenuItem>
                                ) : null}
                                {mayManageMoney ? (
                                  <MenuItem icon={<HandCoins className="size-4" />} onSelect={() => setClearingId(r.id)}>
                                    Clear / adjust debt
                                  </MenuItem>
                                ) : null}
                              </>
                            )}
                          </ActionMenu>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {mayManage && <UnmatchedNames currency={currency} />}

      {editing && (
        <RiderForm
          rider={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {removing && <RemoveRider rider={removing} onClose={() => setRemoving(null)} />}
      {assigningId && rows.find((r) => r.id === assigningId) ? (
        <AssignBikeDialog rider={rows.find((r) => r.id === assigningId)!} onClose={() => setAssigningId(null)} />
      ) : null}
      {clearingId && rows.find((r) => r.id === clearingId) ? (
        <ClearDebtDialog rider={rows.find((r) => r.id === clearingId)!} onClose={() => setClearingId(null)} />
      ) : null}
      {viewing && (
        <RiderProfile rider={viewing} currency={currency} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

// ── Unmatched names ─────────────────────────────────────────────────────────

/**
 * Delivery names that match no rider.
 *
 * Reported, never acted on automatically. CHIDUBAM against CHIDUBEM is 54
 * rides, a rate difference and a ₦15,000 exemption — so a near-miss is only
 * ever a reason for somebody to look.
 */
function UnmatchedNames({ currency }: { currency: string }): ReactNode {
  const unmatched = useUnmatchedNames(true);
  const rows = unmatched.data?.data ?? [];
  if (rows.length === 0) return null;

  return (
    <section>
      <SectionTitle>Names waiting on a decision</SectionTitle>
      <Card>
        <p className="mb-2 text-xs text-slate-500">
          These rides are counted in every delivery report. They accrue no remittance, because
          accrual works from the roster and these names are on no roster. Nothing here is matched
          automatically — two spellings differing by a letter can be two people.
        </p>
        <div className="-mx-3 overflow-x-auto px-3">
          <table className="w-full min-w-[620px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-3 font-medium">As recorded</th>
                <th className="py-2 pr-3 text-right font-medium">Rides</th>
                <th className="py-2 pr-3 text-right font-medium">Gross</th>
                <th className="py-2 pr-3 font-medium">Seen</th>
                <th className="py-2 font-medium">References</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((u) => (
                <tr key={u.riderName}>
                  <td className="py-2 pr-3 font-medium text-slate-900">{u.riderName}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-slate-600">{u.rides}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                    {money(u.gross, currency)}
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-500">
                    {u.firstSeen === u.lastSeen
                      ? (u.firstSeen ?? '—')
                      : `${u.firstSeen ?? '—'} – ${u.lastSeen ?? '—'}`}
                  </td>
                  <td className="py-2 font-mono text-xs text-slate-400">
                    {u.references.slice(0, 3).join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
}

// ── Add and edit ────────────────────────────────────────────────────────────

function RiderForm({
  rider,
  onClose,
}: {
  rider: RiderDirectoryRow | null;
  onClose: () => void;
}): ReactNode {
  const { locations } = useWorkingLocation();
  const create = useCreateRider();
  const update = useUpdateRider();
  // Whether a rider owes weekly remittance is a money decision: only remittance:manage sees it,
  // and the server refuses anyone else who tries to change it.
  const mayManageMoney = useAuth().can('remittance:manage');

  const [canonicalName, setName] = useState(rider?.canonicalName ?? '');
  const [externalId, setExternalId] = useState(rider?.externalId ?? '');
  const [phone, setPhone] = useState(rider?.phone ?? '');
  const [branchId, setBranchId] = useState(rider?.branchId ?? '');
  const [joinedOn, setJoinedOn] = useState(rider?.joinedOn ?? '');
  const [status, setStatus] = useState(rider?.status === 'suspended' ? 'suspended' : 'active');
  const [note, setNote] = useState(rider?.note ?? '');
  const [remittanceExempt, setRemittanceExempt] = useState(rider?.remittanceExempt ?? false);

  const error = create.error ?? update.error;
  const fields = error instanceof ApiError ? error.fields : undefined;

  return (
    <Modal open onClose={onClose} title={rider ? `Edit ${rider.canonicalName}` : 'Add a rider'} size="lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          const rest = {
            canonicalName,
            branchId: branchId || null,
            joinedOn: joinedOn || null,
            note: note || null,
            // Sent only by someone allowed to decide it; otherwise left as it is (new riders: charged).
            ...(mayManageMoney ? { remittanceExempt } : {}),
          };
          if (rider) {
            // A record from before Rider ID and phone were required may still
            // lack them: an edit fills them in, or leaves them as they were.
            update.mutate(
              {
                id: rider.id,
                input: {
                  ...rest,
                  ...(externalId.trim() ? { externalId: externalId.trim() } : {}),
                  ...(phone.trim() ? { phone: normalizeNigerianPhone(phone) ?? phone.trim() } : {}),
                  status: status as 'active' | 'suspended',
                },
              },
              { onSuccess: onClose },
            );
          } else {
            create.mutate(
              { ...rest, externalId: externalId.trim(), phone: normalizeNigerianPhone(phone) ?? phone.trim() },
              { onSuccess: onClose },
            );
          }
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Name"
            required
            error={fields?.canonicalName?.[0]}
            value={canonicalName}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <Input
            label="Rider ID"
            required={!rider || Boolean(rider.externalId)}
            hint="The company's own code, e.g. CH-R008"
            error={fields?.externalId?.[0]}
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
          />
          <Input
            label="Phone"
            required={!rider || Boolean(rider.phone)}
            inputMode="tel"
            placeholder="08030000000"
            error={fields?.phone?.[0]}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Select
            label="Location"
            placeholder="Not recorded"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            options={locations.map((b) => ({ value: b.id, label: b.name }))}
          />
          <Input
            label="Joined"
            type="date"
            hint="Left blank until somebody knows it"
            error={fields?.joinedOn?.[0]}
            value={joinedOn}
            onChange={(e) => setJoinedOn(e.target.value)}
          />
          {rider && (
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              hint="Suspended keeps them on the roster"
              options={[
                { value: 'active', label: 'Active' },
                { value: 'suspended', label: 'Suspended' },
              ]}
            />
          )}
        </div>

        {mayManageMoney && (
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={remittanceExempt}
              onChange={(e) => setRemittanceExempt(e.target.checked)}
            />
            <span>
              Exempt from weekly remittance
              <span className="block text-xs text-slate-500">
                An exempt rider owes no remittance week, whatever rides they complete.
              </span>
            </span>
          </label>
        )}

        <Textarea label="Notes" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />

        {error && !fields ? <ErrorState message={error.message} /> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending || update.isPending}>
            {rider ? 'Save changes' : 'Add rider'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Removal ─────────────────────────────────────────────────────────────────

/**
 * Remove a rider, which deactivates and deletes nothing.
 *
 * The dialog states what survives and what is still attached, because a bike in
 * somebody's hands and a half-paid agreement are decisions rather than side
 * effects. The API refuses until they are acknowledged.
 */
function RemoveRider({
  rider,
  onClose,
}: {
  rider: RiderDirectoryRow;
  onClose: () => void;
}): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');
  const entangled = useRiderEntanglements(rider.id);
  const deactivate = useRemoveRider();
  const [outcome, setOutcome] = useState<'deleted' | 'archived' | null>(null);

  const [effectiveOn, setEffectiveOn] = useState(today);
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const state = entangled.data?.data;
  const fields = deactivate.error instanceof ApiError ? deactivate.error.fields : undefined;
  const needsAck = state ? !state.clear : false;

  return (
    <Modal open onClose={onClose} title={`Remove ${rider.canonicalName}?`} size="lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          deactivate.mutate(
            {
              id: rider.id,
              input: {
                effectiveOn,
                reason,
                ...(needsAck ? { acknowledgeEntanglements: acknowledged } : {}),
              },
            },
            { onSuccess: (r) => setOutcome(r.data.outcome) },
          );
        }}
      >
        {outcome ? (
          <div className="space-y-3">
            <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
              {outcome === 'deleted'
                ? `${rider.canonicalName} was deleted. Nothing was on record for them.`
                : `${rider.canonicalName} was archived. Their history is kept, and they can be reactivated from Archived.`}
            </p>
            <div className="flex justify-end">
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : null}
        {outcome ? null : (
        <>
        <p className="text-sm text-slate-600">
          {state?.hasHistory === false && state.bikes.length === 0
            ? 'Nothing is on record for this rider — no deliveries, charges, payments or agreements — so they will be deleted.'
            : 'They have history, so they will be archived, not deleted. Their deliveries, remittance, payments, hire purchase, bike assignments and tracker history stay exactly as they are — and anything still owed stays owed. They leave the active list, stop accruing remittance from the date below, and can be reactivated from Archived.'}
        </p>

        {needsAck && state && (
          <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="flex items-center gap-1.5 text-sm font-medium text-amber-900">
              <AlertTriangle className="size-4" aria-hidden />
              Still attached to this rider
            </p>
            <ul className="space-y-1 text-sm text-amber-900">
              {state.bikes.map((b) => (
                <li key={b.id}>
                  Bike <strong>{b.registration}</strong>
                  {b.gpsImei ? ` · tracker ${b.gpsImei}` : ''}
                </li>
              ))}
              {state.agreements.map((a) => (
                <li key={a.id}>
                  Hire purchase on <strong>{a.bikeRegistration}</strong> —{' '}
                  {money(a.outstanding, currency)} left
                </li>
              ))}
            </ul>
            <p className="text-xs text-amber-800">
              Reassign the bike or settle the agreement first, or confirm below that they stay
              where they are. Nothing is unlinked automatically.
            </p>
            <label className="flex items-start gap-2 text-sm text-amber-900">
              <input
                type="checkbox"
                className="mt-0.5 size-4 rounded border-amber-300"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              Leave them attached and remove the rider anyway
            </label>
          </div>
        )}

        <Input
          label="Stop accruing remittance from"
          type="date"
          required
          error={fields?.effectiveOn?.[0]}
          hint="Weeks beginning on or after this date will not charge them. Weeks already accrued never change."
          value={effectiveOn}
          onChange={(e) => setEffectiveOn(e.target.value)}
        />

        <Input
          label="Why"
          required
          error={fields?.reason?.[0]}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Left for another company"
        />

        {Number(rider.remittanceOutstanding) > 0 && (
          <p className="text-sm text-slate-600">
            They still owe <strong>{money(rider.remittanceOutstanding, currency)}</strong> in
            remittance. That stays on the books — leaving is not a write-off.
          </p>
        )}

        {deactivate.error && !fields ? <ErrorState message={deactivate.error.message} /> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={
              deactivate.isPending || reason.trim() === '' || (needsAck && !acknowledged)
            }
          >
            Remove rider
          </Button>
        </div>
        </>
        )}
      </form>
    </Modal>
  );
}

// ── Profile ─────────────────────────────────────────────────────────────────

function RiderProfile({
  rider,
  currency,
  onClose,
}: {
  rider: RiderDirectoryRow;
  currency: string;
  onClose: () => void;
}): ReactNode {
  return (
    <Modal open onClose={onClose} title={rider.canonicalName} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Deliveries" count={rider.deliveries} />
          <Stat
            label="Remittance owed"
            value={rider.remittanceOutstanding}
            currency={currency}
            tone={Number(rider.remittanceOutstanding) > 0 ? 'owed' : 'plain'}
          />
          <Stat
            label="Hire purchase"
            value={rider.hirePurchaseOutstanding}
            currency={currency}
            hint={
              rider.hirePurchaseStatus
                ? HIRE_PURCHASE_STATUS_LABELS[rider.hirePurchaseStatus]
                : 'None'
            }
          />
          <div className="rounded-xl border border-slate-200 bg-white p-3.5">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Status</p>
            <div className="mt-1.5">
              <Badge
                tone={
                  rider.status === 'active'
                    ? 'success'
                    : rider.status === 'suspended'
                      ? 'warning'
                      : 'neutral'
                }
              >
                {RIDER_STATUS_LABELS[rider.status]}
              </Badge>
            </div>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-slate-500">Rider ID</dt>
          <dd className="font-mono text-slate-900">{rider.externalId ?? '—'}</dd>
          <dt className="text-slate-500">Phone</dt>
          <dd className="text-slate-900">{rider.phone ?? '—'}</dd>
          <dt className="text-slate-500">Location</dt>
          <dd className="text-slate-900">{rider.branchName ?? '—'}</dd>
          <dt className="text-slate-500">Current bike</dt>
          <dd className="text-slate-900">{rider.bikeRegistration ?? '—'}</dd>
          <dt className="text-slate-500">GPS tracker</dt>
          <dd className="font-mono text-slate-900">{rider.gpsImei ?? '—'}</dd>
          <dt className="text-slate-500">Joined</dt>
          <dd className="text-slate-900">{rider.joinedOn ?? 'Not recorded'}</dd>
          <dt className="text-slate-500">Also written</dt>
          <dd className="text-slate-900">{rider.aliases.join(', ') || '—'}</dd>
          <dt className="text-slate-500">Weekly remittance</dt>
          <dd className="text-slate-900">{rider.remittanceExempt ? 'Exempt' : 'Charged'}</dd>
        </dl>

        {rider.status === 'inactive' && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            Left on <strong>{rider.deactivationEffectiveOn}</strong>
            {rider.deactivationReason ? ` — ${rider.deactivationReason}` : ''}. No remittance
            accrues from that date; everything before it is unchanged.
          </div>
        )}

        {rider.note && <p className="text-sm text-slate-600">{rider.note}</p>}

        <div className="flex justify-end border-t border-slate-200 pt-3">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
